import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Formik, Form, Field, FormikProps, FormikHelpers } from 'formik';

import { useFormatMessage } from 'components/IntlProvider';
import RouterProvider from 'components/RouterProvider/RouterProvider';
import api from 'api/api';

import {
  Modal,
  ModalTitle,
  ModalActions,
  ModalContent,
  AddableInput,
  showToast,
  Button,
} from '@deeptrue-frontend/ui';

const { withRouter } = RouterProvider;

interface AddUsersModalProps extends RouteComponentProps<any> {
  isVisible: boolean;
  anchor?: React.MutableRefObject<undefined>;
  onClose: () => void;
  orgToInvite: string | null;
}

type FormValues = {
  emails: string[];
};

type FormProps = {
  initialValues: FormValues;
} & FormikProps<FormValues>;

const AddUsersModal = ({
  anchor,
  isVisible,
  onClose,
  orgToInvite,
}: AddUsersModalProps) => {
  const t = useFormatMessage();

  const [shouldResetEmails, resetEmails] = useState<boolean>(false);
  const [addedEmails, setEmails] = useState<string[]>([]);

  const clearEmailsList = () => {
    resetEmails(true);
    setEmails([]);
  };

  const inviteUsers = (
    { emails }: { emails: string[] },
    { setErrors, setSubmitting }: FormikHelpers<FormValues>
  ) => {
    const isAnyEmailAdded = emails.length > 0;
    if (!isAnyEmailAdded) {
      return setSubmitting(false);
    }

    api.resources
      .inviteOrganizationUser({
        organization_name: orgToInvite,
        emails: emails,
      })
      .then(({ data }) => {
        const isAbsoluteValid =
          data.invited.length && !data.already_invited.length;

        if (data.already_invited.length) {
          const errorMsg = t(
            'warn.toast.email',
            'Invitation has already been sent to { email } email(s)',
            {
              email: data.already_invited.join(', '),
            }
          );
          setErrors({ emails: errorMsg });
          showToast({
            type: 'warn',
            title: errorMsg,
          });
        }
        if (data.invited.length) {
          if (isAbsoluteValid) onClose();

          showToast({
            type: 'success',
            title: t(
              'success.toast.email',
              'Invitations have been sent to { email }',
              {
                email: data.invited.join(', '),
              }
            ),
          });
        }

        clearEmailsList();
      })
      .catch(() => {
        const errorMsg = t(
          'errors.toast.general',
          'Something went wrong, please contact administrator'
        );

        setErrors({ emails: errorMsg });
        showToast({
          type: 'error',
          title: errorMsg,
        });
      });
  };

  return (
    <Modal
      isVisible={isVisible}
      portalAnchor={anchor}
      onCloseClick={onClose}
      onBackdropClick={onClose}
    >
      <Formik
        initialValues={{ emails: addedEmails }}
        onSubmit={inviteUsers}
        enableReinitialize
      >
        {({ handleSubmit, isSubmitting, errors }: FormProps) => {
          return (
            <Form onSubmit={handleSubmit} data-testid="main-form">
              <ModalTitle>
                {t('tooltip.project.invite', 'Provide email address')}
              </ModalTitle>
              <ModalContent>
                <Field name="emails">
                  {() => (
                    <AddableInput
                      type="email"
                      areResetedItems={shouldResetEmails}
                      customError={(errors.emails as string) || undefined}
                      onAddNew={setEmails}
                      placeholder={t(
                        'input.placeholder.general',
                        'Type or paste and press Enter...'
                      )}
                    />
                  )}
                </Field>
              </ModalContent>
              <ModalActions>
                <Button
                  variant="primary"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {t('pages.project.modal.invite', 'Invite users')}
                </Button>
              </ModalActions>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default withRouter(AddUsersModal);
